import { useState, useEffect } from 'react';
import { Crown, Sparkles, Mic, Brain, FileText, Palette, Check, Loader2, ChevronLeft, ChevronRight, Gift, Zap, Star, Flame, Heart, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios, { API } from '@/utils/api';

// Icon mapping for admin-configured messages
const ICON_MAP = {
  crown: Crown,
  sparkles: Sparkles,
  gift: Gift,
  zap: Zap,
  star: Star,
  fire: Flame,
  heart: Heart,
  megaphone: Megaphone,
};

const UpgradeBanner = ({ onUpgrade, scrollingMessages = null }) => {
  const { user } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Default messages if none provided
  const defaultMessages = [
    {
      id: 'default-upgrade',
      title: 'Unlock Premium',
      subtitle: 'Get the full journaling experience',
      icon: 'crown',
      ctaText: 'Upgrade Now',
      ctaAction: 'upgrade',
      priceText: 'Starting at $6.25/mo billed yearly',
    }
  ];
  
  // Use scrolling messages if provided, otherwise default
  const messages = (scrollingMessages && scrollingMessages.length > 0) ? scrollingMessages : defaultMessages;
  const hasMultipleMessages = messages.length > 1;
  
  // Auto-rotate messages every 5 seconds
  useEffect(() => {
    if (!hasMultipleMessages) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % messages.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [hasMultipleMessages, messages.length]);
  
  // Only show for free tier users
  if (!user || user.membership_tier !== 'free') {
    return null;
  }
  
  const currentMessage = messages[currentIndex];
  const IconComponent = ICON_MAP[currentMessage.icon] || Crown;

  const features = [
    { icon: FileText, text: 'Unlimited journal entries' },
    { icon: Mic, text: 'Unlimited voice typing' },
    { icon: Brain, text: 'Unlimited AI Coach reflections' },
    { icon: Sparkles, text: 'Full mood history & insights' },
  ];
  
  const handlePrev = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => (prev - 1 + messages.length) % messages.length);
      setIsTransitioning(false);
    }, 300);
  };
  
  const handleNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % messages.length);
      setIsTransitioning(false);
    }, 300);
  };
  
  const handleCTA = () => {
    if (currentMessage.ctaAction === 'upgrade' || !currentMessage.ctaAction) {
      onUpgrade();
    } else if (currentMessage.ctaAction === 'link' && currentMessage.ctaLink) {
      window.open(currentMessage.ctaLink, '_blank');
    }
  };

  return (
    <div 
      className="relative overflow-hidden rounded-2xl mb-6"
      style={{
        background: currentMessage.gradientStart && currentMessage.gradientEnd 
          ? `linear-gradient(135deg, ${currentMessage.gradientStart}25 0%, ${currentMessage.gradientEnd}25 100%)`
          : 'linear-gradient(135deg, rgba(223, 18, 131, 0.15) 0%, rgba(255, 119, 1, 0.15) 50%, rgba(147, 51, 234, 0.15) 100%)',
        border: `1px solid ${currentMessage.gradientStart || 'rgba(223, 18, 131, 0.3)'}50`,
      }}
      data-testid="upgrade-banner"
    >
      {/* Animated background sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-2 left-10 w-2 h-2 bg-yellow-400 rounded-full animate-pulse opacity-60" />
        <div className="absolute top-8 right-20 w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-4 left-1/4 w-2 h-2 bg-purple-400 rounded-full animate-pulse opacity-40" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-6 right-1/3 w-1 h-1 bg-orange-400 rounded-full animate-pulse opacity-70" style={{ animationDelay: '0.3s' }} />
      </div>
      
      {/* Navigation arrows for multiple messages */}
      {hasMultipleMessages && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            aria-label="Previous message"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            aria-label="Next message"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </>
      )}

      <div className={`relative p-5 sm:p-6 transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <div className="flex flex-col lg:flex-row items-center gap-5 lg:gap-8">
          
          {/* Left: Icon & Title */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: currentMessage.gradientStart && currentMessage.gradientEnd 
                  ? `linear-gradient(135deg, ${currentMessage.gradientStart} 0%, ${currentMessage.gradientEnd} 100%)`
                  : 'linear-gradient(135deg, #df1283 0%, #FF7901 100%)',
                boxShadow: `0 4px 20px ${currentMessage.gradientStart || '#df1283'}66`,
              }}
            >
              <IconComponent className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--theme-text)' }}>
                {currentMessage.title}
              </h3>
              <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                {currentMessage.subtitle}
              </p>
            </div>
          </div>

          {/* Center: Features or Custom Description */}
          <div className="flex flex-wrap justify-center gap-3 flex-1">
            {currentMessage.description ? (
              <p className="text-center text-sm" style={{ color: 'var(--theme-text)' }}>
                {currentMessage.description}
              </p>
            ) : (
              features.map((feature) => (
                <div 
                  key={feature.text}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: 'var(--theme-text)',
                  }}
                >
                  <feature.icon className="w-4 h-4 text-[#df1283]" />
                  <span>{feature.text}</span>
                </div>
              ))
            )}
          </div>

          {/* Right: CTA */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            {currentMessage.discountBadge && (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-500/20 text-green-400 border border-green-500/50 mb-1">
                {currentMessage.discountBadge}
              </span>
            )}
            <Button
              onClick={handleCTA}
              className="px-8 py-3 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
              style={{
                background: currentMessage.gradientStart && currentMessage.gradientEnd 
                  ? `linear-gradient(135deg, ${currentMessage.gradientStart} 0%, ${currentMessage.gradientEnd} 100%)`
                  : 'linear-gradient(135deg, #df1283 0%, #FF7901 100%)',
                color: 'white',
              }}
              data-testid="upgrade-btn"
            >
              {currentMessage.ctaText || 'Upgrade Now'}
            </Button>
            <p className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>
              {currentMessage.priceText || 'Starting at $6.25/mo billed yearly'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Dot indicators for multiple messages */}
      {hasMultipleMessages && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {messages.map((msg, idx) => (
            <button
              key={msg.id || `dot-${idx}`}
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentIndex(idx);
                  setIsTransitioning(false);
                }, 300);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'bg-white w-4' 
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to message ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Pricing Modal Component - Can be shown when user clicks Upgrade
export const PricingModal = ({ isOpen, onClose, onSelectPlan }) => {
  const [loading, setLoading] = useState(null); // null or 'monthly' or 'yearly'
  
  if (!isOpen) return null;

  const handleSelectPlan = async (planId) => {
    setLoading(planId);
    try {
      // SECURITY: Auth via httpOnly cookies
      const response = await axios.post(
        `${API}/stripe/create-checkout`,
        { price_type: planId }
      );
      
      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Failed to start checkout. Please try again.');
      }
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 9.99,
      period: 'month',
      description: 'Flexible monthly billing',
      features: [
        'Unlimited journal entries',
        'Unlimited voice typing',
        'Unlimited AI Coach reflections',
        'Full mood history & PDF export',
        'Custom themes & badges',
      ],
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 75,
      period: 'year',
      pricePerMonth: 6.25,
      savings: '37% OFF',
      popular: true,
      description: 'Best value - save $45/year',
      features: [
        'Everything in Monthly',
        'Priority support',
        'Early access to new features',
      ],
    },
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border-2 border-[#df1283]/50 rounded-2xl p-6 sm:p-8 max-w-3xl w-full"
        onClick={e => e.stopPropagation()}
        data-testid="pricing-modal"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Crown className="w-8 h-8 text-[#FF7901]" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Choose Your Plan</h2>
          </div>
          <p className="text-[#FDF2F8]/60">Unlock the full UnCloned journaling experience</p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {plans.map(plan => (
            <div 
              key={plan.id}
              className={`relative rounded-xl p-6 transition-all ${loading ? 'opacity-70' : 'hover:scale-[1.02]'} cursor-pointer ${
                plan.popular 
                  ? 'bg-gradient-to-br from-[#df1283]/20 to-[#FF7901]/20 border-2 border-[#df1283]' 
                  : 'bg-[#4A0A66]/30 border border-[#4A0A66]'
              }`}
              onClick={() => !loading && handleSelectPlan(plan.id)}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#df1283] to-[#FF7901] rounded-full text-xs font-bold text-white">
                  MOST POPULAR
                </div>
              )}
              
              {plan.savings && (
                <div className="absolute top-4 right-4 px-2 py-0.5 bg-green-500/20 border border-green-500/50 rounded-full text-xs font-bold text-green-400">
                  {plan.savings}
                </div>
              )}

              <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
              <p className="text-sm text-[#FDF2F8]/50 mb-4">{plan.description}</p>
              
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold text-white">${plan.price}</span>
                <span className="text-[#FDF2F8]/50">/{plan.period}</span>
              </div>
              
              {plan.pricePerMonth && (
                <p className="text-sm text-[#FF7901] mb-4">
                  Just ${plan.pricePerMonth}/month
                </p>
              )}

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-[#FDF2F8]/80">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button 
                className={`w-full py-3 font-semibold ${
                  plan.popular 
                    ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90'
                    : 'bg-[#4A0A66] text-white hover:bg-[#5A1A76]'
                }`}
                disabled={loading !== null}
              >
                {loading === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  `Select ${plan.name}`
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-[#FDF2F8]/40">
            Cancel anytime. Secure payment via Stripe.
          </p>
          <button 
            onClick={onClose}
            className="mt-4 text-sm text-[#FDF2F8]/60 hover:text-white transition-colors"
            disabled={loading !== null}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeBanner;
